<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Sales Collection Report"
      />
    </div>

    <div class="px-2">
      <DateQuerySetter
          btnTitle="Go"
          @onClickGo="onClickGo"
      />
    </div>

    <div class="d-flex justify-content-end px-2 mt-1">
      <button
          @click="exportReport"
          class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1"
      >
        Export
      </button>
      <button
          @click="goToPrint"
          class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1"
      >
        Print
      </button>
    </div>

    <div class="col-12 pt-2">
      <div class="px-2">
        <ListTable :tableItems="tableItems" :totals="totals" :mopHeads="mopHeads" />
      </div>
    </div>
    <div class="px-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination" />
    </div>
  </div>
  <Loader v-if="loading" />
</template>

<script setup>
import { ref, inject, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import TitleButton from '@/components/atom/TitleButton';
import Loader from '@/components/atom/LoaderComponent.vue';
import Pagination from '@/components/atom/Pagination';
import ListTable from '@/components/atom/super-shop/sales-collection-report/ListTable.vue';
import handleInventory from '@/services/modules/inventory';
import handleCompany from '@/services/modules/company';
import handleBarcode from '@/services/modules/barcode';
import pdfPrinter from '@/services/utils/pdf/salesCollectionReportPdf';

const router     = useRouter();
const route      = useRoute();
const store      = useStore();
const loading    = ref(false);
const tableItems = ref([]);
const totals     = ref({});
const mopHeads   = ref([]);
const offset     = ref(20);
const companyInfo = ref({});

const { fetchSalesCollectionReport } = handleInventory();
const { fetchCompanyInfo } = handleCompany();
const { fetchBarcode } = handleBarcode();
const { exportToPDF } = pdfPrinter();

const showError = inject('showError');

const companyId = computed(() => route.params.companyId);
const startDate = computed(() => route.query.start);
const endDate   = computed(() => route.query.end);
const page      = computed(() => route.query.page);

const getQuery = () => {
  let query = '?company_id=' + companyId.value;
  if (startDate.value) query += '&start_date=' + startDate.value;
  if (endDate.value)   query += '&end_date='   + endDate.value;
  query += '&offset=' + offset.value;
  query += '&page='   + (page.value ?? 1);
  return query;
};

const onClickGo = async () => {
  loading.value    = true;
  tableItems.value = [];
  totals.value     = {};
  mopHeads.value   = [];

  await fetchSalesCollectionReport(getQuery())
    .then((res) => {
      if (!res.status) {
        store.commit("resetPagination")
        return;
      }
      tableItems.value = res.data.rows;
      totals.value     = res.data.totals;
      mopHeads.value   = res.data.mop_heads;
      setPagination(res.data)
    })
    .catch(() => {
      showError('Failed to fetch report');
    })
    .finally(() => {
      loading.value = false;
    });
};

const goToPrint = async () => {
  const userName = store.state.user.user.name;
  loading.value  = true;
  try {
    const query = getQuery() + '&is_all=1';
    const res   = await fetchSalesCollectionReport(query);
    if (!res.status) return;

    const qrcodeData = JSON.stringify({
      created_by: userName,
      created_date_time: new Date().toISOString()
    });
    const barcodeRes = await fetchBarcode(`?code=sales-collection-report&qrcode=${qrcodeData}`);
    exportToPDF(companyInfo.value, res.data, startDate.value, endDate.value, barcodeRes.qrcode);
  } catch (e) {
    showError('Failed to generate PDF');
  } finally {
    loading.value = false;
  }
};

function setPagination(data) {
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query);
  routeQuery.page = page;
  await router.push({ path: route.path, query: routeQuery });
  await onClickGo();
};

const exportReport = () => {
  const token = localStorage.getItem('token');
  let query = `?company_id=${companyId.value}&is_all=1&is_export=1`;
  if (startDate.value) query += `&start_date=${startDate.value}`;
  if (endDate.value)   query += `&end_date=${endDate.value}`;
  query += `&_token=${token}`;
  const url = `${process.env.VUE_APP_BASE_URL}/export/reports/sales-collection-report${query}`;
  const a = document.createElement('a');
  a.setAttribute('href', url);
  a.click();
};

fetchCompanyInfo('?company_id=' + companyId.value).then((res) => {
  if (res.status) companyInfo.value = res.data;
});
</script>
